import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import Modal from './Modal';
import { decodeHtmlEntities } from '../utils/decodeHtmlEntities';
import { parseJsonResponse, describeFailure, type ParsedResponse } from '../utils/parseJsonResponse';
import { useCartItems } from '../hooks/useCartItems';
import { useShippingQuote } from '../hooks/useShippingQuote';
import type { CartItem, ShippingMethod } from '../types/shipping';

interface CartProps {
  countryCode: string;
  stateCode: string;
  onOrderComplete: (data: {
    orderId: number;
    username: string;
    items: EnrichedCartItem[];
    subtotal: number;
    shippingCost: number;
    total: number;
    shippingMethodTitle: string;
    shipping: {
      firstName: string;
      lastName: string;
      address: string;
      city: string;
      state: string;
      postcode: string;
      email: string;
      phone: string;
    };
    billing?: {
      firstName: string;
      lastName: string;
      company?: string;
      nif?: string;
      address: string;
      city: string;
      state: string;
      postcode: string;
    };
    comments?: string;
  }) => void;
}

interface ShippingForm {
  firstName: string;
  lastName: string;
  address: string;
  city: string;
  state: string;
  postcode: string;
  email: string;
  emailConfirm: string;
  phone: string;
  useCustomBilling: boolean;
  billingCompany: string;
  billingNif: string;
  billingFirstName: string;
  billingLastName: string;
  billingAddress: string;
  billingCity: string;
  billingState: string;
  billingPostcode: string;
  password: string;
  shippingMethod: string;
  comments?: string;
}

interface EnrichedCartItem extends CartItem {
  name?: string;
  price?: number;
  image?: string;
  taxClass?: string;
}

interface Product {
  id: number;
  parentId?: number;
  name: string;
  image: string;
  price: string;
  sku: string;
  stockStatus: string;
  shippingClass?: string;
  taxClass?: string;
}

interface ProductsResponse {
  products: Product[];
  language: string;
}

interface State {
  code: string;
  name: string;
}

interface SelectOption {
  value: string;
  label: string;
}

export default function Cart({ countryCode, stateCode, onOrderComplete }: CartProps) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitLockRef = useRef(false);
  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm<ShippingForm>({
    defaultValues: {
      state: stateCode,
      postcode: localStorage.getItem('tempPostcode') || '',
      city: localStorage.getItem('tempCity') || ''
    }
  });

  // Estats pel modal
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [modalTitle, setModalTitle] = useState('');
  const [modalType, setModalType] = useState<'success' | 'error' | 'info' | 'warning'>('info');
  const [modalCallback, setModalCallback] = useState<(() => void) | undefined>(undefined);

  const useCustomBilling = watch('useCustomBilling');
  const selectedShippingMethod = watch('shippingMethod');
  const postcode = watch('postcode');
  const city = watch('city');
  const state = watch('state');
  const { items: cartItems, setQuantity: setCartItemQuantity, removeItem: removeCartItem, clear: clearCart } = useCartItems();
  const [enrichedCartItems, setEnrichedCartItems] = useState<EnrichedCartItem[]>([]);
  const [subtotal, setSubtotal] = useState(0);

  // Clau d'idempotència de l'intent de compra en curs. Es genera només quan
  // cal (a l'enviar la comanda) i es reenvia sense canvis en reintents, així
  // el servidor pot reconèixer que és el mateix intent i no duplicar la
  // comanda si el proxy va tallar la resposta anterior abans d'hora.
  const idempotencyKeyRef = useRef<string | null>(null);
  const getIdempotencyKey = useCallback(() => {
    if (!idempotencyKeyRef.current) {
      idempotencyKeyRef.current =
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random()}`;
    }
    return idempotencyKeyRef.current;
  }, []);

  // Si el contingut del carret canvia, ja no es tracta del mateix intent de
  // compra: reiniciem la clau perquè un nou enviament creï una comanda nova
  // en lloc de recuperar-ne una d'anterior amb articles diferents.
  const cartItemsSignature = useMemo(
    () => JSON.stringify(cartItems.map(i => [i.productId, i.quantity]).sort()),
    [cartItems]
  );
  useEffect(() => {
    idempotencyKeyRef.current = null;
  }, [cartItemsSignature]);

  const [showBottleWarning, setShowBottleWarning] = useState(false);
  const [bottleWarningInfo, setBottleWarningInfo] = useState<{ bottleCount: number, neededToAdd: number }>({ bottleCount: 0, neededToAdd: 0 });
  const [bottleCount, setBottleCount] = useState<{ standard: number, magnum: number, total: number }>({ standard: 0, magnum: 0, total: 0 });

  // Funció per mostrar modals
  const showModal = (title: string, message: string, type: 'success' | 'error' | 'info' | 'warning' = 'info', callback?: () => void) => {
    setModalTitle(title);
    setModalMessage(message);
    setModalType(type);
    setModalCallback(callback);
    setModalOpen(true);
  };

  // Converteix una resposta fallida en una línia de detall tècnic llegible.
  // Sense això l'usuari només veu "hi ha hagut un error" i qui atén la
  // incidència no pot saber si va ser un tall del proxy, un temps d'espera
  // esgotat o un error real de l'aplicació sense mirar la consola.
  const describeFailureText = (parsed: ParsedResponse<unknown>) => {
    const failure = describeFailure(parsed);
    const reasonText =
      failure.reason === 'empty' ? t('cart.reasonEmpty')
      : failure.reason === 'html' ? t('cart.reasonHtml')
      : failure.reason === 'server' ? (failure.message || t('cart.reasonMalformed'))
      : t('cart.reasonMalformed');

    const parts = [`HTTP ${failure.status}`, reasonText];
    if (failure.code) parts.push(failure.code);

    return t('cart.technicalDetail', { detail: parts.join(' · ') });
  };

  const { data: productsResponse } = useQuery<ProductsResponse>({
    queryKey: ['products', countryCode, i18n.language],
    queryFn: async () => {
      const response = await fetch(`/api/products/${countryCode}?lang=${i18n.language}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (!response.ok) {
        throw new Error(t('products.error'));
      }
      return response.json();
    }
  });

  // Memoitzat perquè no canviï de referència a cada render i desestabilitzi
  // els useMemo/useCallback que en depenen (enviament, comptador d'ampolles...).
  const products = useMemo(() => productsResponse?.products || [], [productsResponse]);

  const { data: states, isLoading: isLoadingStates } = useQuery<State[]>({
    queryKey: ['states', countryCode, i18n.language],
    queryFn: async () => {
      const response = await fetch(`/api/states/${countryCode}?lang=${i18n.language}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (!response.ok) {
        throw new Error(t('states.error'));
      }
      return response.json();
    }
  });

  // Consulta per saber si el país actual requereix comprar en múltiples de 6
  const { data: countryInfo } = useQuery<{ requiresSixBottleMultiples?: boolean }>({
    queryKey: ['countryRequiresSixBottleMultiples', countryCode],
    queryFn: async () => {
      const response = await fetch('/api/countries', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (!response.ok) {
        throw new Error('Error obtenint informació del país');
      }
      const countries = await response.json();
      const country = countries.find((c: any) => c.code === countryCode);
      return country || {};
    }
  });

  // Determinar si el país actual no té províncies/estats
  const hasNoStates = states && states.length === 0;

  const stateOptions: SelectOption[] = states 
    ? states.map(state => ({ value: state.code, label: decodeHtmlEntities(state.name) }))
    : [];

  useEffect(() => {
    // Inicialitzem la província amb el valor rebut de la selecció de país NOMÉS
    // si l'usuari encara no n'ha triat cap. No podem dependre de `stateOptions`
    // (es recrea a cada render): ho faria a cada render i clobbaria la selecció
    // que l'usuari fa al checkout, impedint canviar la província.
    if (!state && stateCode && ((states && states.length > 0) || stateCode === 'NA')) {
      setValue('state', stateCode);
    }
  }, [states, stateCode, state, setValue]);

  // Font única de veritat pel càlcul d'enviament: es recalcula automàticament
  // cada vegada que canvia el carret, el país, la província, el codi postal
  // o la ciutat (aquests dos últims amb debounce). Sense keepPreviousData:
  // mentre es recalcula, `methods` és buit i mai es mostra un cost obsolet.
  const { methods, isFetching: isFetchingShipping, refetch: refetchShipping } = useShippingQuote({
    countryCode,
    state: state || stateCode,
    postcode,
    city,
    items: cartItems,
    products,
    enabled: cartItems.length > 0
  });

  // Persistim el codi postal/ciutat introduïts perquè altres pantalles
  // (mini-carret, llistat de productes) que encara no tenen aquestes dades
  // puguin fer servir els mateixos valors provisionals un cop l'usuari els
  // ha introduït al checkout.
  useEffect(() => {
    if (postcode) localStorage.setItem('tempPostcode', postcode);
  }, [postcode]);

  useEffect(() => {
    if (city) localStorage.setItem('tempCity', city);
  }, [city]);

  // Si el mètode seleccionat al formulari ja no existeix a la llista rebuda
  // (canvi de província, de carret, etc.) triem el primer disponible.
  useEffect(() => {
    const stillValid = methods.some((method: ShippingMethod) => method.id === selectedShippingMethod);
    if (!stillValid) {
      setValue('shippingMethod', methods[0]?.id ?? '');
    }
  }, [methods, selectedShippingMethod, setValue]);

  // Funció per comprovar si la comanda compleix amb els requisits de múltiples de 6 ampolles
  const calculateBottleCount = useCallback(() => {
    let standardBottles = 0;
    let magnumBottles = 0;

    cartItems.forEach(item => {
      const product = products.find(p => p.id === item.productId);
      if (product) {
        const shippingClass = product.shippingClass || '';
        const decodedName = decodeHtmlEntities(product.name).toLowerCase();

        if (
          shippingClass.toLowerCase().includes('magnum') ||
          decodedName.includes('magnum') ||
          decodedName.includes('150cl') ||
          product.sku?.toLowerCase().includes('magnum')
        ) {
          magnumBottles += item.quantity;
        }
        else if (
          shippingClass.toLowerCase().includes('std-ca') ||
          shippingClass.toLowerCase().includes('ampolla') ||
          shippingClass.toLowerCase().includes('botella') ||
          shippingClass.toLowerCase().includes('std') ||
          (!shippingClass && (
            decodedName.includes('75cl') ||
            decodedName.includes('0,75l') ||
            decodedName.includes('0.75l'))
          )
        ) {
          standardBottles += item.quantity;
        }
        else {
          if (
            decodedName.includes('vi ') ||
            decodedName.includes('vino') ||
            decodedName.includes('wine') ||
            product.sku?.toLowerCase().includes('vi')
          ) {
            standardBottles += item.quantity;
          }
        }
      }
    });

    const totalBottles = standardBottles + (magnumBottles * 2);
    setBottleCount({ standard: standardBottles, magnum: magnumBottles, total: totalBottles });
    return { standard: standardBottles, magnum: magnumBottles, total: totalBottles };
  }, [cartItems, products]);

  const validateSixBottleMultiples = useCallback(() => {
    if (!countryInfo?.requiresSixBottleMultiples) {
      return { valid: true, bottleCount: 0 };
    }

    // Contar ampolles estàndard i magnum
    let totalBottleCount = 0;
    
    // Mostrar tota la informació dels productes al carret per depuració
    console.log("====== DEBUGGING BOTTLE MULTIPLES ======");
    console.log("Tots els productes disponibles:", products);
    console.log("Items al carret:", cartItems);
    
    cartItems.forEach(item => {
      const product = products.find(p => p.id === item.productId);
      if (product) {
        // Mostrar tota la informació del producte per depuració
        console.log("Producte trobat:", product);
        
        // Obtenir la shipping class del producte
        const shippingClass = product.shippingClass || '';
        
        // Debug: Imprimir la shipping class per identificar els valors reals
        console.log(`Producte ${product.id} (${product.name}): shipping class = "${shippingClass}"`);
        
        // SOLUCIÓ 1: Comprovem si és ampolla magnum utilitzant múltiples criteris
        const decodedName = decodeHtmlEntities(product.name).toLowerCase();
        if (
          shippingClass.toLowerCase().includes('magnum') || 
          decodedName.includes('magnum') ||
          decodedName.includes('150cl') ||
          product.sku?.toLowerCase().includes('magnum')
        ) {
          console.log(`  → Ampolla MAGNUM detectada (${item.quantity} x 2 = ${item.quantity * 2} ampolles)`);
          totalBottleCount += item.quantity * 2;
        } 
        // SOLUCIÓ 2: Si és ampolla estàndard (més criteris de detecció)
        else if (
          shippingClass.toLowerCase().includes('std-ca') || 
          shippingClass.toLowerCase().includes('ampolla') ||
          shippingClass.toLowerCase().includes('botella') ||
          shippingClass.toLowerCase().includes('std') ||
          // Si no té shipping class però el nom conté indicadors d'ampolla estàndard
          (!shippingClass && (
            decodedName.includes('75cl') ||
            decodedName.includes('0,75l') ||
            decodedName.includes('0.75l'))
          )
        ) {
          console.log(`  → Ampolla estàndard detectada (${item.quantity} ampolles)`);
          totalBottleCount += item.quantity;
        }
        // Si no coincideix amb cap dels patrons anteriors, intentar detectar per nom/sku
        else {
          // Darrer intent: si el nom o SKU conté "vi" o "vino" o "wine", és probablement una ampolla estàndard
          if (
            decodedName.includes('vi ') || 
            decodedName.includes('vino') || 
            decodedName.includes('wine') ||
            product.sku?.toLowerCase().includes('vi')
          ) {
            console.log(`  → Ampolla estàndard detectada per nom/SKU (${item.quantity} ampolles)`);
            totalBottleCount += item.quantity;
          } else {
            console.log(`  → No és una ampolla, no es compta`);
          }
        }
      }
    });
    
    console.log(`Total d'ampolles computades: ${totalBottleCount}`);
    console.log("====== FI DEBUGGING BOTTLE MULTIPLES ======");
    
    // Validar que el número total d'ampolles és múltiple de 6
    const isValid = totalBottleCount % 6 === 0 && totalBottleCount > 0;
    
    return {
      valid: isValid,
      bottleCount: totalBottleCount
    };
  }, [cartItems, products, countryInfo]);

  useEffect(() => {
    if (products && cartItems.length > 0) {
      let newSubtotal = 0;
      const updatedItems = cartItems.map(item => {
        const product = products.find(p => p.id === item.productId);
        if (product) {
          const price = parseFloat(product.price);
          newSubtotal += price * item.quantity;
          return {
            ...item,
            name: product.name,
            price: price,
            image: product.image,
            taxClass: product.taxClass || ''
          };
        }
        return item;
      });

      setEnrichedCartItems(updatedItems);
      setSubtotal(newSubtotal);
      calculateBottleCount();
    } else {
      setEnrichedCartItems([]);
      setSubtotal(0);
      setBottleCount({ standard: 0, magnum: 0, total: 0 });
    }
  }, [products, cartItems, calculateBottleCount]);

  // El cost d'enviament es deriva sempre del mètode seleccionat dins la
  // llista rebuda del servidor: mai és un valor "guardat" d'una consulta
  // anterior, així que en desaparèixer els mètodes (carret buit, canvi de
  // província...) torna a 0 automàticament.
  const shippingCost = useMemo(() => {
    const method = methods.find((m: ShippingMethod) => m.id === selectedShippingMethod);
    if (!method) return 0;
    const cost = parseFloat(String(method.cost).replace(',', '.'));
    return isNaN(cost) ? 0 : cost;
  }, [methods, selectedShippingMethod]);

  const total = useMemo(() => subtotal + shippingCost, [subtotal, shippingCost]);

  const handleRemoveItem = (productId: number) => {
    removeCartItem(productId);
  };

  const handleUpdateQuantity = (productId: number, quantity: number) => {
    if (quantity <= 0) {
      handleRemoveItem(productId);
      return;
    }

    setCartItemQuantity(productId, quantity);
  };

  const handleStateChange = (selectedOption: SelectOption | null) => {
    if (selectedOption) {
      setValue('state', selectedOption.value);
      // En canviar de província cal reiniciar el mètode d'enviament: la nova
      // zona pot oferir mètodes diferents; el useEffect que sincronitza
      // `methods` triarà el primer disponible quan arribin els nous.
      setValue('shippingMethod', '');
    }
  };


  // Comprovar els requisits de múltiples de 6 ampolles cada vegada que canvia el carret
  useEffect(() => {
    if (countryInfo?.requiresSixBottleMultiples && cartItems.length > 0) {
      const validation = validateSixBottleMultiples();
      if (!validation.valid) {
        const remainder = validation.bottleCount % 6;
        const neededToAdd = remainder === 0 ? 6 : 6 - remainder;
        
        setShowBottleWarning(true);
        setBottleWarningInfo({
          bottleCount: validation.bottleCount,
          neededToAdd: neededToAdd
        });
      } else {
        setShowBottleWarning(false);
      }
    } else {
      setShowBottleWarning(false);
    }
  }, [cartItems, countryInfo, validateSixBottleMultiples]);

  const onSubmit = async (data: ShippingForm) => {
    if (submitLockRef.current) {
      return;
    }

    submitLockRef.current = true;
    setIsSubmitting(true);
    try {
      if (cartItems.length === 0) {
        showModal(t('cart.error'), t('cart.emptyCart'), 'error');
        setIsSubmitting(false);
        return;
      }

      // Comprovar si hi ha mètodes d'enviament disponibles i que el seleccionat
      // sigui un dels rebuts en l'última consulta (mai un de desfasat).
      if (methods.length === 0 || !methods.some((method: ShippingMethod) => method.id === data.shippingMethod)) {
        showModal(t('cart.error'), t('cart.noShippingMethodsAvailable'), 'error');
        setIsSubmitting(false);
        return;
      }

      // Validar múltiples de 6 ampolles si el país ho requereix
      if (countryInfo?.requiresSixBottleMultiples) {
        const validation = validateSixBottleMultiples();
        if (!validation.valid) {
          const remainder = validation.bottleCount % 6;
          const neededToAdd = remainder === 0 ? 6 : 6 - remainder;
          
          showModal(
            t('cart.error'), 
            `${t('cart.sixBottleMultiplesRequired')} ${t('cart.currentBottleCount')}: ${validation.bottleCount}. ${t('cart.bottlesToAdd')}: ${neededToAdd}.`, 
            'error'
          );
          setIsSubmitting(false);
          return;
        }
      }
      
      const selectedMethod = methods.find((method: ShippingMethod) => method.id === data.shippingMethod);
      const billingData = {
        first_name: useCustomBilling ? data.billingFirstName : data.firstName,
        last_name: useCustomBilling ? data.billingLastName : data.lastName,
        company: data.billingCompany,
        address_1: useCustomBilling ? data.billingAddress : data.address,
        city: useCustomBilling ? data.billingCity : data.city,
        state: useCustomBilling ? data.billingState : data.state,
        postcode: useCustomBilling ? data.billingPostcode : data.postcode,
        country: countryCode,
        email: data.email,
        phone: data.phone,
        nif: data.billingNif
      };
      
      const orderData = {
        password: data.password, // Enviem la contrasenya per validació
        idempotencyKey: getIdempotencyKey(),
        countryCode,
        language: i18n.language,
        items: enrichedCartItems.map(({ productId, quantity, price, taxClass }) => ({
          productId,
          quantity,
          price,
          taxClass: taxClass || ''
        })),
        shipping: {
          first_name: data.firstName,
          last_name: data.lastName,
          address_1: data.address,
          city: data.city,
          state: data.state,
          postcode: data.postcode,
          country: countryCode,
          email: data.email,
          phone: data.phone
        },
        billing: billingData,
        shipping_method: selectedMethod ? {
          id: selectedMethod.id,
          title: selectedMethod.title,
          cost: selectedMethod.cost
        } : undefined,
        ...(data.comments && { customer_note: data.comments })
      };

      let parsed;
      try {
        const response = await fetch('/api/orders', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify(orderData)
        });
        parsed = await parseJsonResponse<any>(response);
      } catch (networkError) {
        // El propi fetch ha fallat (xarxa, timeout...): no sabem si el
        // servidor ha arribat a crear la comanda. Tractem-ho igual que un
        // resultat indeterminat.
        console.error('Error de xarxa enviant la comanda:', networkError);
        const detail = networkError instanceof Error
          ? `${t('cart.reasonNetwork')} (${networkError.message})`
          : t('cart.reasonNetwork');
        showModal(
          t('cart.orderUncertainTitle'),
          `${t('cart.orderUncertain')}\n\n${t('cart.technicalDetail', { detail })}`,
          'warning'
        );
        return;
      }

      if (parsed.kind === 'json' && parsed.ok) {
        // Eliminar només els items del carret (no el país)
        clearCart();
        localStorage.removeItem('tempPostcode');
        localStorage.removeItem('tempCity');

        // Cap resultat d'enviament ha de sobreviure a una comanda ja creada.
        queryClient.removeQueries({ queryKey: ['shipping'] });

        // Comanda confirmada: el proper enviament serà un intent nou.
        idempotencyKeyRef.current = null;

        const responseData = parsed.data;

        // Mostrar resum imprimible de la comanda
        onOrderComplete({
          orderId: responseData.orderId,
          username: localStorage.getItem('username') || '',
          items: enrichedCartItems,
          subtotal,
          shippingCost,
          total,
          shippingMethodTitle: selectedMethod?.title || data.shippingMethod || '',
          shipping: {
            firstName: data.firstName,
            lastName: data.lastName,
            address: data.address,
            city: data.city,
            state: data.state,
            postcode: data.postcode,
            email: data.email,
            phone: data.phone,
          },
          ...(data.useCustomBilling && {
            billing: {
              firstName: data.billingFirstName,
              lastName: data.billingLastName,
              company: data.billingCompany,
              nif: data.billingNif,
              address: data.billingAddress,
              city: data.billingCity,
              state: data.billingState,
              postcode: data.billingPostcode,
            }
          }),
          comments: data.comments,
        });
        return;
      }

      // Fallada segura: el servidor ha respost abans de cridar WooCommerce,
      // per tant la comanda segur que no s'ha creat. L'usuari pot corregir
      // i tornar a provar amb normalitat.
      const isSafeFailure =
        parsed.kind === 'json' &&
        !parsed.ok &&
        (parsed.status === 400 || parsed.data?.code === 'INVALID_PASSWORD' || parsed.data?.code === 'SESSION_CLOSED');

      if (isSafeFailure && parsed.kind === 'json') {
        const responseData = parsed.data;
        if (responseData?.code === 'INVALID_PASSWORD') {
          // Contrasenya incorrecta però la sessió continua oberta: netegem el camp
          setValue('password', '');
          const remaining = responseData.remainingAttempts ?? 0;
          showModal(
            t('cart.error'),
            remaining > 0
              ? t('cart.wrongPassword', { count: remaining })
              : t('cart.wrongPasswordLastChance'),
            'error'
          );
          return;
        }
        if (responseData?.code === 'SESSION_CLOSED') {
          showModal(t('cart.error'), t('cart.sessionClosed'), 'error');
          return;
        }
        showModal(t('cart.error'), responseData?.error || t('cart.orderError'), 'error');
        return;
      }

      // El servidor ja està processant una comanda amb aquesta mateixa clau
      // d'idempotència. No és un error i tornar a prémer ara no serviria de
      // res: només cal esperar que acabi.
      if (parsed.kind === 'json' && parsed.data?.code === 'ORDER_IN_PROGRESS') {
        showModal(
          t('cart.orderUncertainTitle'),
          `${t('cart.orderInProgress')}\n\n${describeFailureText(parsed)}`,
          'warning'
        );
        return;
      }

      // Resultat indeterminat: pàgina HTML del proxy, cos buit, o un error
      // 500/desconegut. No sabem si WooCommerce ha arribat a crear la
      // comanda, així que NO netegem el carret ni avisem `onOrderComplete`.
      // Si l'usuari torna a prémer Confirmar, la mateixa clau d'idempotència
      // farà que el servidor recuperi la comanda ja creada en lloc de
      // duplicar-la.
      const logBody = parsed.kind === 'non-json' ? parsed.body : JSON.stringify(parsed.data).slice(0, 300);
      console.error(`Resultat indeterminat en crear la comanda (status ${parsed.status}):`, logBody);
      showModal(
        t('cart.orderUncertainTitle'),
        `${t('cart.orderUncertain')}\n\n${describeFailureText(parsed)}`,
        'warning'
      );
    } catch (error) {
      // Error inesperat del propi client (no de la resposta del servidor).
      // Mostrem una explicació entenedora i, a sota, el detall tècnic, en
      // lloc del missatge cru de l'excepció.
      console.error('Error inesperat enviant la comanda:', error);
      const detail = error instanceof Error ? `${error.name}: ${error.message}` : String(error);
      showModal(
        t('cart.error'),
        `${t('cart.orderError')}\n\n${t('cart.technicalDetail', { detail })}`,
        'error'
      );
    } finally {
      submitLockRef.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto py-8">
      {/* Modal per mostrar missatges */}
      <Modal 
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          if (modalCallback) modalCallback();
        }}
        title={modalTitle}
        message={modalMessage}
        type={modalType}
      />

      <h2 className="text-2xl font-bold mb-6">{t('cart.checkout')}</h2>          <div className="flex justify-between items-center mb-4">
            <button
              type="button"
              onClick={() => navigate('/')}
              className="flex items-center text-gray-900 hover:text-blue-600 transition-colors duration-200"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
                <path d="M19 12H5M12 19l-7-7 7-7"/>
              </svg>
              {t('cart.continueShopping')}
            </button>

        <span className="text-sm text-gray-500">
          {bottleCount.magnum > 0
            ? t('cart.bottlesAndMagnums', {
                bottles: `${bottleCount.standard} ${bottleCount.standard === 1 ? t('cart.bottleSingular') : t('cart.bottlePlural')}`,
                magnums: `${bottleCount.magnum} ${bottleCount.magnum === 1 ? t('cart.magnumSingular') : t('cart.magnumPlural')}`
              })
            : bottleCount.standard > 0
            ? t('cart.bottlesOnly', {
                count: bottleCount.standard,
                label: bottleCount.standard === 1 ? t('cart.bottleSingular') : t('cart.bottlePlural')
              })
            : `${cartItems.length} ${cartItems.length === 1 ? t('cart.productSingular') : t('cart.productPlural')} ${t('cart.inCart')}`
          }
        </span>
      </div>
      
      {showBottleWarning && (
        <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mb-4">
          <p>{t('cart.sixBottleWarning', { bottleCount: bottleWarningInfo.bottleCount, neededToAdd: bottleWarningInfo.neededToAdd })}</p>
        </div>
      )}

      <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
        <h3 className="text-lg font-medium mb-4">{t('cart.summary')}</h3>
        
        {cartItems.length === 0 ? (
          <div className="text-gray-500 text-center py-4">
            {t('cart.emptyCart')} <button 
              onClick={() => navigate('/')} 
              className="text-black hover:text-blue-600 transition-colors"
            >
              {t('cart.returnToShop')}
            </button>
          </div>
        ) : (
          <>
            <div className="space-y-4">
              {enrichedCartItems.map(item => (
                <div key={item.productId} className="flex items-center justify-between border-b pb-2">
                  <div className="flex items-center">
                    {item.image && (
                      <img 
                        src={item.image} 
                        alt={item.name ? decodeHtmlEntities(item.name) : t('products.product')} 
                        className="h-14 w-14 rounded object-contain bg-white border border-gray-200 mr-3"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = '/placeholder.png';
                        }}
                      />
                    )}
                    <div>
                      <div className="font-medium">{item.name ? decodeHtmlEntities(item.name) : `${t('products.product')} #${item.productId}`}</div>
                      <div className="text-sm text-gray-500">
                        {t('products.price')}: {item.price?.toFixed(2) || '0.00'}€ x {item.quantity}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="font-medium">
                      {((item.price || 0) * item.quantity).toFixed(2)}€
                    </div>
                    <div className="flex items-center">
                      <button 
                        onClick={() => handleUpdateQuantity(item.productId, item.quantity - 1)}
                        className="px-2 py-1 bg-gray-200 rounded-l"
                      >
                        -
                      </button>
                      <span className="px-3 py-1 bg-gray-100">{item.quantity}</span>
                      <button 
                        onClick={() => handleUpdateQuantity(item.productId, item.quantity + 1)}
                        className="px-2 py-1 bg-gray-200 rounded-r"
                      >
                        +
                      </button>
                    </div>
                    <button
                      onClick={() => handleRemoveItem(item.productId)}
                      className="text-red-500 hover:text-red-700 p-1 rounded-full hover:bg-red-50 transition-colors"
                      aria-label={t('cart.removeItem')}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="pt-3">
              <div className="flex justify-between items-center mb-2">
                <div className="text-gray-600">{t('cart.subtotal')}</div>
                <div className="font-medium">{subtotal.toFixed(2)}€</div>
              </div>
              
              <div className="flex justify-between items-center mb-2">
                <div className="text-gray-600">{t('cart.shipping')}</div>
                <div className="font-medium">{shippingCost.toFixed(2)}€</div>
              </div>
              
              <div className="flex justify-between items-center border-t pt-3 mt-3">
                <div className="font-medium text-lg">{t('cart.total')}</div>
                <div className="font-bold text-lg">{total.toFixed(2)}€</div>
              </div>
            </div>
          </>
        )}
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" autoComplete="off">
        <div className="bg-white shadow-sm rounded-lg p-6">
          <h3 className="text-lg font-medium mb-4">{t('cart.shippingDetails')}</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">{t('cart.firstName')}*</label>
              <input
                type="text"
                {...register('firstName', { required: t('cart.requiredField') })}
                autoComplete="off"
                className="mt-1 block w-full border rounded-md shadow-sm py-2 px-3"
              />
              {errors.firstName && (
                <p className="text-red-500 text-xs mt-1">{errors.firstName.message}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">{t('cart.lastName')}*</label>
              <input
                type="text"
                {...register('lastName', { required: t('cart.requiredField') })}
                autoComplete="off"
                className="mt-1 block w-full border rounded-md shadow-sm py-2 px-3"
              />
              {errors.lastName && (
                <p className="text-red-500 text-xs mt-1">{errors.lastName.message}</p>
              )}
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700">{t('cart.address')}*</label>
              <input
                type="text"
                {...register('address', { required: t('cart.requiredField') })}
                autoComplete="off"
                className="mt-1 block w-full border rounded-md shadow-sm py-2 px-3"
              />
              {errors.address && (
                <p className="text-red-500 text-xs mt-1">{errors.address.message}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">{t('cart.city')}*</label>
              <input
                type="text"
                {...register('city', { required: t('cart.requiredField') })}
                autoComplete="off"
                className="mt-1 block w-full border rounded-md shadow-sm py-2 px-3"
              />
              {errors.city && (
                <p className="text-red-500 text-xs mt-1">{errors.city.message}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">{t('cart.state')}*</label>
              {isLoadingStates ? (
                <div className="mt-1 block w-full p-2.5 border rounded-md shadow-sm text-gray-500">
                  {t('cart.loading')}
                </div>
              ) : hasNoStates ? (
                <div className="mt-1 block w-full p-2.5 border rounded-md shadow-sm bg-gray-50 text-gray-500">
                  {t('country.noStates')}
                  <input type="hidden" {...register('state')} value="NA" />
                </div>
              ) : (
                <>
                  <Select
                    options={stateOptions}
                    onChange={handleStateChange}
                    className="mt-1 block w-full"
                    classNamePrefix="select"
                    placeholder={t('cart.selectState')}
                    noOptionsMessage={() => t('cart.noStatesFound')}
                    value={stateOptions.find(option => option.value === state)}
                    styles={{
                      control: (base) => ({
                        ...base,
                        minHeight: '42px',
                        borderColor: '#D1D5DB',
                        '&:hover': {
                          borderColor: '#2563EB'
                        }
                      })
                    }}
                  />
                  <input type="hidden" {...register('state', { required: !hasNoStates ? t('cart.stateRequired') : false })} />
                </>
              )}
              {errors.state && (
                <p className="text-red-500 text-xs mt-1">{errors.state.message}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">{t('cart.postcode')}*</label>
              <input
                type="text"
                {...register('postcode', { 
                  required: t('cart.requiredField')
                })}
                autoComplete="off"
                className="mt-1 block w-full border rounded-md shadow-sm py-2 px-3"
              />
              {errors.postcode && (
                <p className="text-red-500 text-xs mt-1">{errors.postcode.message}</p>
              )}
              <button
                type="button"
                onClick={() => refetchShipping()}
                className="mt-2 text-sm text-gray-900 hover:text-blue-600 transition-colors duration-200 flex items-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {t('cart.calculateShipping')}
              </button>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">{t('cart.phone')}*</label>
              <input
                type="tel"
                {...register('phone', { required: t('cart.requiredField') })}
                autoComplete="off"
                className="mt-1 block w-full border rounded-md shadow-sm py-2 px-3"
              />
              {errors.phone && (
                <p className="text-red-500 text-xs mt-1">{errors.phone.message}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">{t('cart.email')}*</label>
              <input
                type="email"
                {...register('email', {
                  required: t('cart.requiredField'),
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: t('cart.emailInvalid')
                  }
                })}
                autoComplete="off"
                className="mt-1 block w-full border rounded-md shadow-sm py-2 px-3"
              />
              {errors.email && (
                <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">{t('cart.emailConfirm')}*</label>
              <input
                type="email"
                {...register('emailConfirm', {
                  required: t('cart.requiredField'),
                  validate: (value) => value === watch('email') || t('cart.emailMismatch')
                })}
                autoComplete="off"
                className="mt-1 block w-full border rounded-md shadow-sm py-2 px-3"
              />
              {errors.emailConfirm && (
                <p className="text-red-500 text-xs mt-1">{errors.emailConfirm.message}</p>
              )}
            </div>
            <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700">{t('cart.comments')}</label>
                <textarea
                  {...register('comments')}
                  rows={3}
                  placeholder={t('cart.commentsPlaceholder')}
                  autoComplete="off"
                  className="mt-1 block w-full border rounded-md shadow-sm py-2 px-3 resize-none"
                />
            </div>
          </div>
        </div>

        <div className="bg-white shadow-sm rounded-lg p-6">
          <h3 className="text-lg font-medium mb-4">{t('cart.shippingMethod')}</h3>
          
          {isFetchingShipping ? (
            <div className="text-center py-4">{t('cart.loadingShippingOptions')}</div>
          ) : methods.length > 0 ? (
            <div className="space-y-2">
              {methods.map((method: ShippingMethod) => {
                let costDisplay = '0.00';
                try {
                  let costValue = method.cost;
                  if (typeof costValue === 'string') {
                    costValue = costValue.replace(',', '.');
                  }
                  const cost = parseFloat(costValue);
                  costDisplay = isNaN(cost) ? '0.00' : cost.toFixed(2);
                } catch (e) {
                  console.error(`${t('shipping.errorProcessingCost')} ${method.id}:`, e);
                }
                
                return (
                  <div key={method.id} className="flex items-start p-3 border rounded-md">
                    <input
                      type="radio"
                      id={method.id}
                      value={method.id}
                      {...register('shippingMethod', { required: t('cart.shippingMethodRequired') })}
                      className="mt-1 h-4 w-4 text-blue-600"
                    />
                    <label htmlFor={method.id} className="ml-3 cursor-pointer flex-grow">
                      <div className="flex justify-between">
                        <span className="font-medium">{decodeHtmlEntities(method.title)}</span>
                        <span className="font-medium">{costDisplay}€</span>
                      </div>
                      {method.description && (
                        <span className="text-sm text-gray-500 block">{decodeHtmlEntities(method.description)}</span>
                      )}
                    </label>
                  </div>
                );
              })}
              
              {errors.shippingMethod && (
                <p className="text-red-500 text-sm">{errors.shippingMethod.message}</p>
              )}
            </div>
          ) : (
            <div className="text-center text-gray-500">
              {postcode && city && state ? 
                t('cart.noShippingMethods') :
                t('cart.completeShippingInfo')
              }
            </div>
          )}
        </div>

        <div className="bg-white shadow-sm rounded-lg p-6">
          <div className="flex items-center mb-4">
            <input
              type="checkbox"
              {...register('useCustomBilling')}
              className="h-4 w-4 text-blue-600"
            />
            <label className="ml-2 block text-sm text-gray-900">
              {t('cart.differentBillingAddress')}
            </label>
          </div>

          {useCustomBilling && (
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">{t('cart.billingCompany')}</label>
                <input
                  type="text"
                  {...register('billingCompany')}
                  autoComplete="off"
                  className="mt-1 block w-full border rounded-md shadow-sm py-2 px-3"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">{t('cart.billingNif')}</label>
                <input
                  type="text"
                  {...register('billingNif')}
                  autoComplete="off"
                  className="mt-1 block w-full border rounded-md shadow-sm py-2 px-3"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">{t('cart.billingFirstName')}*</label>
                <input
                  type="text"
                  {...register('billingFirstName', { required: useCustomBilling ? t('cart.requiredField') : false })}
                  autoComplete="off"
                  className="mt-1 block w-full border rounded-md shadow-sm py-2 px-3"
                />
                {errors.billingFirstName && (
                  <p className="text-red-500 text-xs mt-1">{errors.billingFirstName.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">{t('cart.billingLastName')}*</label>
                <input
                  type="text"
                  {...register('billingLastName', { required: useCustomBilling ? t('cart.requiredField') : false })}
                  autoComplete="off"
                  className="mt-1 block w-full border rounded-md shadow-sm py-2 px-3"
                />
                {errors.billingLastName && (
                  <p className="text-red-500 text-xs mt-1">{errors.billingLastName.message}</p>
                )}
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700">{t('cart.billingAddress')}*</label>
                <input
                  type="text"
                  {...register('billingAddress', { required: useCustomBilling ? t('cart.requiredField') : false })}
                  autoComplete="off"
                  className="mt-1 block w-full border rounded-md shadow-sm py-2 px-3"
                />
                {errors.billingAddress && (
                  <p className="text-red-500 text-xs mt-1">{errors.billingAddress.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">{t('cart.billingCity')}*</label>
                <input
                  type="text"
                  {...register('billingCity', { required: useCustomBilling ? t('cart.requiredField') : false })}
                  autoComplete="off"
                  className="mt-1 block w-full border rounded-md shadow-sm py-2 px-3"
                />
                {errors.billingCity && (
                  <p className="text-red-500 text-xs mt-1">{errors.billingCity.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">{t('cart.billingState')}*</label>
                {hasNoStates ? (
                  <div className="mt-1 block w-full p-2.5 border rounded-md shadow-sm bg-gray-50 text-gray-500">
                    {t('country.noStates')}
                    <input type="hidden" {...register('billingState')} value="NA" />
                  </div>
                ) : (
                  <>
                    <Select
                      options={stateOptions}
                      onChange={(selectedOption) => {
                        if (selectedOption) {
                          setValue('billingState', selectedOption.value);
                        }
                      }}
                      className="mt-1 block w-full"
                      classNamePrefix="select"
                      placeholder={t('cart.selectState')}
                      noOptionsMessage={() => t('cart.noStatesFound')}
                      styles={{
                        control: (base) => ({
                          ...base,
                          minHeight: '42px',
                          borderColor: '#D1D5DB',
                          '&:hover': {
                            borderColor: '#2563EB'
                          }
                        })
                      }}
                    />
                    <input
                      type="hidden"
                      {...register('billingState', { required: useCustomBilling ? t('cart.stateRequired') : false })}
                    />
                  </>
                )}
                {errors.billingState && (
                  <p className="text-red-500 text-xs mt-1">{errors.billingState.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">{t('cart.billingPostcode')}*</label>
                <input
                  type="text"
                  {...register('billingPostcode', { required: useCustomBilling ? t('cart.requiredField') : false })}
                  autoComplete="off"
                  className="mt-1 block w-full border rounded-md shadow-sm py-2 px-3"
                />
                {errors.billingPostcode && (
                  <p className="text-red-500 text-xs mt-1">{errors.billingPostcode.message}</p>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="bg-white shadow-sm rounded-lg p-6">
          <h3 className="text-lg font-medium mb-4">{t('cart.confirmOrder')}</h3>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('cart.confirmPassword')}</label>
            <input
              type="password"
              {...register('password', { required: t('cart.requiredField') })}
              autoComplete="new-password"
              className="mt-1 block w-full border rounded-md shadow-sm py-2 px-3"
            />
            {errors.password && (
              <p className="text-red-500 text-xs mt-1">{errors.password.message}</p>
            )}
          </div>
        </div>

        <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-lg font-medium text-gray-700">{t('cart.summary')}</span>
              <span className="text-lg font-semibold text-gray-900">
                {bottleCount.magnum > 0
                  ? t('cart.bottlesAndMagnums', {
                      bottles: `${bottleCount.standard} ${bottleCount.standard === 1 ? t('cart.bottleSingular') : t('cart.bottlePlural')}`,
                      magnums: `${bottleCount.magnum} ${bottleCount.magnum === 1 ? t('cart.magnumSingular') : t('cart.magnumPlural')}`
                    })
                  : bottleCount.standard > 0
                  ? t('cart.bottlesOnly', {
                      count: bottleCount.standard,
                      label: bottleCount.standard === 1 ? t('cart.bottleSingular') : t('cart.bottlePlural')
                    })
                  : `${cartItems.length} ${cartItems.length === 1 ? t('cart.productSingular') : t('cart.productPlural')}`
                }
              </span>
            </div>

            {showBottleWarning && (
              <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4">
                <p>{t('cart.sixBottleWarning', { bottleCount: bottleWarningInfo.bottleCount, neededToAdd: bottleWarningInfo.neededToAdd })}</p>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="px-4 py-2 border rounded-md text-gray-600 hover:bg-gray-50"
          >
            {t('cart.back')}
          </button>
          <button
            type="submit"
            disabled={
              isSubmitting ||
              isFetchingShipping ||
              cartItems.length === 0 ||
              methods.length === 0 ||
              !methods.some((method: ShippingMethod) => method.id === selectedShippingMethod)
            }
            className="px-4 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800 disabled:opacity-50"
          >
            {isSubmitting ? t('cart.processing') : t('cart.checkout')}
          </button>
        </div>
      </form>
    </div>
  );
}